import React, { useEffect, useRef } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import TopoJSON from 'ol/format/TopoJSON';
import { Style, Fill, Stroke, Circle as CircleStyle, Text, Icon } from 'ol/style';
import { useCustomLayerTopoJSON } from '../../../hooks/useCustomLayers';

const CustomLayer = ({ map, layerConfig, zIndex = 10 }) => {
  const layerRef = useRef(null);
  const { topoJSON, isLoading } = useCustomLayerTopoJSON(layerConfig.id);

  // Cache for patterns to avoid recreating canvas for every feature
  const patternCache = useRef({});

  // Helper to create diagonal line pattern
  const createDiagonalPattern = (color) => {
    if (patternCache.current[color]) {
      return patternCache.current[color];
    }

    const canvas = document.createElement('canvas');
    const size = 11;
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    
    context.strokeStyle = color;
    context.lineWidth = 1.5;
    context.beginPath();
    // Draw diagonal line from bottom-left to top-right
    context.moveTo(0, size);
    context.lineTo(size, 0);
    context.stroke();
    
    const pattern = context.createPattern(canvas, 'repeat');
    patternCache.current[color] = pattern;
    return pattern;
  };

  // Helper to create styles based on config
  const createStyle = (feature) => {
    const config = layerConfig.style_config || {};
    const type = feature.getGeometry().getType();
    const props = feature.getProperties();

    // Base styles
    const strokeColor = config.strokeColor || 'blue';
    const strokeWidth = config.strokeWidth || 2;
    
    // Handle fill pattern
    let fillStyle;
    if (config.fillPattern === 'diagonal') {
      const patternColor = config.fillColor || strokeColor;
      fillStyle = new Fill({ 
        color: createDiagonalPattern(patternColor)
      });
    } else {
      const fillColor = config.fillColor || 'rgba(0, 0, 255, 0.1)';
      fillStyle = new Fill({ color: fillColor });
    }

    const pointRadius = config.pointRadius || 5;
    
    // Label styles
    const showLabels = config.showLabels || false;
    const labelField = config.labelField || 'name';
    const labelText = showLabels && props[labelField] ? String(props[labelField]) : '';

    if (type === 'Point' || type === 'MultiPoint') {
      // Check if this should be an office icon
      const isOffice = (layerConfig?.name && String(layerConfig.name).toLowerCase().includes('office')) || 
                       (layerConfig?.name && String(layerConfig.name).toLowerCase().includes('pah')) ||
                       config?.icon === 'office' ||
                       props?.type === 'office' ||
                       String(layerConfig?.id) === '3'; 

      if (isOffice) {
        return new Style({
          image: new Icon({
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: '/office-icon.png', // Use public path directly
            scale: 0.024 // Scale for new asset
          }),
          text: showLabels ? new Text({
            text: labelText,
            offsetY: -13,
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
            priority: 1000
          }) : undefined,
          zIndex: 1000
        });
      }
      
      return new Style({
        image: new CircleStyle({
          radius: pointRadius,
          fill: fillStyle,
          stroke: new Stroke({ color: strokeColor, width: strokeWidth })
        }),
        text: showLabels ? new Text({
          text: labelText,
          offsetY: -10,
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }) : undefined
      });
    }

    if (type === 'LineString' || type === 'MultiLineString') {
      let lineDash = config.lineDash;
      let lineCap = config.lineCap || 'butt';

      // Special handling for frontline layers to ensure "high dpi" dotted look
      if (layerConfig.name && layerConfig.name.toLowerCase().includes('frontline')) {
        // Use a finer dash pattern (3px dash, 5px space) for higher DPI look
        lineDash = [2, 3];
        lineCap = 'butt';
      }

      return new Style({
        stroke: new Stroke({
          color: strokeColor,
          width: strokeWidth,
          lineDash: lineDash, // e.g., [10, 10] or [3, 5] for frontline
          lineCap: lineCap
        }),
        text: showLabels ? new Text({
          text: labelText,
          placement: 'line',
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 2.5 })
        }) : undefined
      });
    }

    if (type === 'Polygon' || type === 'MultiPolygon') {
      // Default to diagonal if not specified, as requested for control areas
      if (!config.fillPattern) {
        // User requested: darkred, transparent, no border stroke
        const patternColor = 'rgba(139, 0, 0, 0.3)'; // Dark red with high transparency
        
        fillStyle = new Fill({ 
          color: createDiagonalPattern(patternColor)
        });

        // Return style with no border stroke
        return new Style({
          fill: fillStyle,
          text: showLabels ? new Text({
            text: labelText,
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          }) : undefined
        });
      }

      return new Style({
        stroke: new Stroke({
          color: strokeColor,
          width: strokeWidth
        }),
        fill: fillStyle,
        text: showLabels ? new Text({
          text: labelText,
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }) : undefined
      });
    }

    // Default style
    return new Style({
      fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
      stroke: new Stroke({ color: '#3399CC', width: 1.25 }),
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: 'orange' }),
        stroke: new Stroke({ color: 'red', width: 1 })
      })
    });
  };

  useEffect(() => {
    if (!map) return;

    // Create layer if it doesn't exist
    if (!layerRef.current) {
      const source = new VectorSource();
      const vectorLayer = new VectorLayer({
        source: source,
        zIndex: layerConfig.z_index || zIndex,
        opacity: layerConfig.opacity || 1.0,
        visible: layerConfig.is_visible !== false,
        declutter: true
      });
      
      // Set custom properties for identification
      vectorLayer.set('id', `custom-layer-${layerConfig.id}`);
      vectorLayer.set('type', 'custom');
      vectorLayer.set('title', layerConfig.name);

      map.addLayer(vectorLayer);
      layerRef.current = vectorLayer;
    }

    // Update visibility and opacity
    layerRef.current.setVisible(layerConfig.is_visible !== false);
    layerRef.current.setOpacity(layerConfig.opacity || 1.0);
    layerRef.current.setZIndex(layerConfig.z_index || zIndex);

    // Apply style function
    layerRef.current.setStyle(createStyle);

    return () => {
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, layerConfig, zIndex]);

  // Update data when topoJSON changes
  useEffect(() => {
    if (!layerRef.current || !topoJSON || isLoading) return;

    const source = layerRef.current.getSource();
    source.clear();

    // TopoJSON reading
    if (topoJSON) {
      // TopoJSON can contain multiple "objects" (layers). 
      // topojson-server usually puts the input layer under 'layer' key if we passed { layer: geoJSON }
      // The format reader needs to know the projection.
      const features = new TopoJSON().readFeatures(topoJSON, {
        featureProjection: map.getView().getProjection()
      });
      source.addFeatures(features);
    }
  }, [topoJSON, isLoading, map]);

  return null;
};

export default CustomLayer;
